import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/Header.css';
import { FiMenu, FiX, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import logo from '../assets/logo.webp';
import AudioPlayer from './AudioPlayer';
import { getEvents } from '../services/events';

const Header = () => {
    const { t, i18n } = useTranslation();
    const [currentLang, setCurrentLang] = useState(i18n.language || 'es');
    const [activeSection, setActiveSection] = useState('inicio');
    const [events, setEvents] = useState([]);
    const [showEvents, setShowEvents] = useState(false);
    const location = useLocation();
    const isHomePage = location.pathname === '/';
    const navigate = useNavigate();

    const languages = [
        { code: 'es', name: 'ES' },
        { code: 'en', name: 'EN' },
        { code: 'it', name: 'IT' },
        { code: 'fr', name: 'FR' }
    ];

    const changeLanguage = (langCode) => {
        i18n.changeLanguage(langCode);
        setCurrentLang(langCode);
    };

    // Cargar eventos
    useEffect(() => {
        const loadEvents = async () => {
            try {
                // Verificar si la API está disponible
                if (!process.env.REACT_APP_API_URL) {
                    console.log('Header: API no configurada, no se mostrarán eventos');
                    setEvents([]);
                    return;
                }

                console.log('Header: Iniciando carga de eventos...');
                const response = await getEvents();
                console.log('Header: Respuesta de eventos recibida:', response);
                setEvents(response.eventos || []);
                console.log('Header: Eventos actualizados en el estado:', response.eventos || []);
            } catch (error) {
                console.error('Header: Error al cargar eventos:', error);
                // En caso de error, no mostrar eventos
                setEvents([]);
            }
        };

        loadEvents();
        // Actualizar eventos cada 5 minutos solo si la API está disponible
        if (process.env.REACT_APP_API_URL) {
            const interval = setInterval(loadEvents, 300000);
            return () => clearInterval(interval);
        }
    }, []);

    useEffect(() => {
        if (!isHomePage) {
            document.body.classList.add('page-scrolled');
        } else {
            document.body.classList.remove('page-scrolled');
        }
    }, [location, isHomePage]);

    // Detectar la sección activa durante el scroll solo en la página principal
    useEffect(() => {
        if (!isHomePage) return;
        
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            
            const sections = [
                { id: 'inicio', offset: 0 },
                { id: 'historia', offset: document.getElementById('historia')?.offsetTop - 100 || 0 },
                { id: 'servicios', offset: document.getElementById('servicios')?.offsetTop - 100 || 0 },
                { id: 'aplicaciones', offset: document.getElementById('aplicaciones')?.offsetTop - 100 || 0 },
                { id: 'unete', offset: document.getElementById('unete')?.offsetTop - 100 || 0 }
            ];
            
            // Encontrar la sección activa
            for (let i = sections.length - 1; i >= 0; i--) {
                if (scrollPosition >= sections[i].offset) {
                    setActiveSection(sections[i].id);
                    break;
                }
            }
        };
        
        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Ejecutar al montar para establecer la sección inicial
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [location, isHomePage]);

    // Inicialización de Google Analytics
    useEffect(() => {
        // Crear el script de GA
        const script1 = document.createElement('script');
        script1.async = true;
        script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-JHD5N59SQ5';
        document.head.appendChild(script1);

        // Inicializar GA
        window.dataLayer = window.dataLayer || [];
        function gtag(){window.dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-JHD5N59SQ5');

        // Cleanup
        return () => {
            document.head.removeChild(script1);
        };
    }, []);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        
        return `${day}/${month}/${year}`;
    };

    return (
        <header>
            <nav>
                <div className="nav-left">
                    <div className="status-container">
                        <AudioPlayer />
                        <div className="language-selector">
                            {languages.map(lang => (
                                <button
                                    key={lang.code}
                                    className={`lang-btn ${currentLang === lang.code ? 'active' : ''}`}
                                    onClick={() => changeLanguage(lang.code)}
                                >
                                    {lang.name}
                                </button>
                            ))}
                        </div>
                        <Link to="/el-desguace" className="podcast-link">
                            <div className="platform-icon">
                                <i className="fas fa-podcast"></i>
                            </div>
                            <span>El Desguace</span>
                        </Link>
                    </div>
                </div>
                <div className="logo">
                    <Link to="/">
                        <img 
                            src={logo} 
                            alt="Corvus Nocta Logo" 
                        />
                    </Link>
                </div>
                <div className="nav-right">
                    <div className="nav-links">
                        {isHomePage ? (
                            <>
                                <a href="#inicio" className={activeSection === 'inicio' ? 'active' : ''}>{t('header.inicio')}</a>
                                <a href="#historia" className={activeSection === 'historia' ? 'active' : ''}>{t('header.historia')}</a>
                                <a href="#servicios" className={activeSection === 'servicios' ? 'active' : ''}>{t('header.servicios')}</a>
                                <a href="#aplicaciones" className={activeSection === 'aplicaciones' ? 'active' : ''}>{t('header.aplicaciones')}</a>
                                <Link to="/lore" className={location.pathname === '/lore' ? 'active' : ''}>{t('header.lore')}</Link>
                                <a href="#unete" className={activeSection === 'unete' ? 'active' : ''}>{t('header.unete')}</a>
                            </>
                        ) : (
                            <>
                                <Link to="/#inicio">{t('header.inicio')}</Link>
                                <Link to="/#historia">{t('header.historia')}</Link>
                                <Link to="/#servicios">{t('header.servicios')}</Link>
                                <Link to="/#aplicaciones">{t('header.aplicaciones')}</Link>
                                <Link to="/lore" className="active">{t('header.lore')}</Link>
                                <Link to="/#unete">{t('header.unete')}</Link>
                            </>
                        )}
                    </div>
                </div>
            </nav>
            {events.length > 0 && (
                <div className="events-dropdown">
                    <button 
                        className="events-toggle"
                        onClick={() => setShowEvents(!showEvents)}
                    >
                        <span>{t('header.events', 'Eventos')} ({events.length})</span>
                        {showEvents ? <FiChevronUp /> : <FiChevronDown />}
                    </button>
                    {showEvents && (
                        <div className="events-list">
                            {events.map(event => (
                                <div key={event.id || event._id} className="event-item">
                                    <div className="event-header">
                                        <span className="event-title">{event.nombre}</span>
                                        <span className="event-date">{formatDate(event.fecha_encuentro)}</span>
                                    </div>
                                    {event.tags_gremio && event.tags_gremio.length > 0 && (
                                        <div className="event-tags">
                                            {event.tags_gremio.map((tag, index) => (
                                                <span key={index} className="event-tag">{tag}</span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </header>
    );
};

export default Header; 